import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { customInstance } from "@/api/axios-instance";
import { hasObstacleGeometry, type ObstacleCheck } from "@/lib/obstacles";

/**
 * Obstacle check of a pizza: the stored photo_quality_evaluation.checks.obstacle_in_pizza when it carries the
 * segmentation (masks, coverage), otherwise the backend computes it on demand from the stored photo + crust
 * mask (GET /api/admin/pizzas/{id}/obstacles: no GPU, nothing persisted, the stored verdict stays).
 * Only fetched when `enabled`; with `enabled` false it still returns what another caller has fetched.
 * `delayMs` holds the request back until the same pizza has been shown that long (paging through pizzas
 * then costs the backend nothing). `preview` = the result is the on-demand one.
 */
export function usePizzaObstacles(
  pizzaId: string | null | undefined,
  stored: ObstacleCheck | null | undefined,
  enabled: boolean,
  delayMs = 0,
) {
  const useStored = hasObstacleGeometry(stored);
  const [settledId, setSettledId] = useState<string | null | undefined>(delayMs > 0 ? null : pizzaId);
  useEffect(() => {
    if (delayMs <= 0) {
      setSettledId(pizzaId);
      return;
    }
    const timer = setTimeout(() => setSettledId(pizzaId), delayMs);
    return () => clearTimeout(timer);
  }, [pizzaId, delayMs]);
  const query = useQuery({
    queryKey: [`/api/admin/pizzas/${pizzaId}/obstacles`],
    queryFn: ({ signal }) =>
      customInstance<ObstacleCheck>({ url: `/api/admin/pizzas/${pizzaId}/obstacles`, method: "GET", signal }),
    enabled: enabled && !!pizzaId && !useStored && (delayMs <= 0 || settledId === pizzaId),
    staleTime: 10 * 60 * 1000,
    retry: false,
  });
  return {
    obstacles: useStored ? (stored as ObstacleCheck) : (query.data ?? null),
    preview: !useStored && !!query.data,
    loading: !useStored && query.isFetching,
    error: !useStored && query.isError,
  };
}
